import { useEffect, useCallback, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import MDButton from "components/MDButton";
import MDBox from "components/MDBox";

/**
 * Custom hook for blocking navigation when there are unsaved changes
 * Works with React Router NavLink components and MUI Dashboard
 *
 * @param {boolean} hasUnsavedChanges - Whether there are unsaved changes
 * @param {string} message - Message to show in the confirmation dialog
 * @returns {Object} - Navigation utilities and dialog control
 */
export const useUnsavedChangesGuard = (
  hasUnsavedChanges,
  message = "You have unsaved changes. Are you sure you want to leave this page?"
) => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentPathRef = useRef(location.pathname);

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);

  // Handle browser tab closing or refreshing
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (!hasUnsavedChanges) return;

      e.preventDefault();
      e.returnValue = message;
      return message;
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [hasUnsavedChanges, message]);

  // Handle dialog confirmation
  const handleConfirm = useCallback(() => {
    setDialogOpen(false);

    if (pendingNavigation) {
      if (pendingNavigation.type === "navigate") {
        navigate(pendingNavigation.to, pendingNavigation.options);
        currentPathRef.current =
          typeof pendingNavigation.to === "string"
            ? pendingNavigation.to
            : pendingNavigation.to.pathname;
      } else if (pendingNavigation.type === "location") {
        // Update reference for the path we're allowing
        currentPathRef.current = pendingNavigation.pathname;
      }
    }

    setPendingNavigation(null);
  }, [navigate, pendingNavigation]);

  // Handle dialog cancel
  const handleCancel = useCallback(() => {
    setDialogOpen(false);

    if (pendingNavigation && pendingNavigation.type === "location") {
      // User cancelled, navigate back to the previous path
      navigate(currentPathRef.current, { replace: true });
    }

    setPendingNavigation(null);
  }, [navigate, pendingNavigation]);

  // Handle React Router navigation using location changes
  useEffect(() => {
    // Skip the initial render
    if (currentPathRef.current === location.pathname) {
      return;
    }

    // If we have unsaved changes and location changed, confirm navigation
    if (hasUnsavedChanges) {
      setPendingNavigation({
        type: "location",
        pathname: location.pathname,
      });
      setDialogOpen(true);
    } else {
      // No unsaved changes, update the current path reference
      currentPathRef.current = location.pathname;
    }
  }, [location, hasUnsavedChanges]);

  // Capture clicks on NavLink and regular links before they trigger navigation
  useEffect(() => {
    if (!hasUnsavedChanges) return;

    const handleNavLinkClick = (e) => {
      // Find closest NavLink or <a> element
      const navLink = e.target.closest("a");
      if (!navLink) return;

      // Get the href/to attribute
      const href = navLink.getAttribute("href");
      if (!href) return;

      // Skip anchor links and download links
      if (href.startsWith("#") || navLink.hasAttribute("download")) return;

      // Skip external links (those will be caught by the other handler)
      if (navLink.origin && navLink.origin !== window.location.origin) return;

      // Skip if the link points to the current path
      if (href === currentPathRef.current) return;

      // Skip links that are already handling confirmation in their own way
      if (navLink.classList.contains("no-confirm")) return;

      // Check if this is an internal React Router link that should be intercepted
      // Common React Router navigation patterns
      const isReactRouterLink =
        // Manual check for NavLink (from react-router-dom)
        (navLink.classList.contains("active") && !navLink.hasAttribute("target")) ||
        // For NavLink
        navLink.getAttribute("data-rr-ui-event-key") ||
        // For Link
        navLink.getAttribute("data-reactrouter") ||
        // For MDX and other frameworks
        (href.startsWith("/") && !navLink.hasAttribute("target"));

      if (isReactRouterLink) {
        setPendingNavigation({
          type: "navigate",
          to: href,
        });
        setDialogOpen(true);
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
    };

    // We use capture phase to intercept before React Router's event listeners
    document.addEventListener("click", handleNavLinkClick, true);

    return () => {
      document.removeEventListener("click", handleNavLinkClick, true);
    };
  }, [hasUnsavedChanges]);

  // For programmatic navigation
  const navigateWithConfirmation = useCallback(
    (to, options) => {
      if (!hasUnsavedChanges) {
        navigate(to, options);
        currentPathRef.current = typeof to === "string" ? to : to.pathname;
      } else {
        setPendingNavigation({
          type: "navigate",
          to,
          options,
        });
        setDialogOpen(true);
      }
    },
    [hasUnsavedChanges, navigate]
  );

  // Dialog component to be displayed
  const UnsavedChangesDialog = useCallback(() => {
    return (
      <Dialog
        open={dialogOpen}
        onClose={handleCancel}
        aria-labelledby="unsaved-changes-dialog-title"
        aria-describedby="unsaved-changes-dialog-description"
      >
        <MDBox sx={{ maxWidth: 450 }}>
          <DialogTitle id="unsaved-changes-dialog-title">Unsaved Changes</DialogTitle>
          <DialogContent>
            <DialogContentText id="unsaved-changes-dialog-description">{message}</DialogContentText>
          </DialogContent>
          <DialogActions sx={{ mt: 2 }}>
            <MDButton
              fullWidth
              onClick={handleCancel}
              variant="outlined"
              color="black"
              sx={{
                borderRadius: "8px",
                padding: "10px",
                border: "1px solid #D0D5DD",
                textDecoration: "lowercase",
                boxShadow: "0px 1px 2px 0px rgba(16, 24, 40, 0.05)",
                textTransform: "none",
                fontSize: "16px",
                fontWeight: 500,
              }}
            >
              Cancel
            </MDButton>

            <MDButton
              fullWidth
              variant="contained"
              color="success"
              sx={{
                borderRadius: "8px",
                textTransform: "none",
                fontSize: "16px",
                fontWeight: 500,
              }}
              onClick={handleConfirm}
            >
              Leave
            </MDButton>
          </DialogActions>
        </MDBox>
      </Dialog>
    );
  }, [dialogOpen, handleCancel, handleConfirm, message]);

  return {
    navigateWithConfirmation,
    hasUnsavedChanges,
    UnsavedChangesDialog,
  };
};
